import { DAY_NAMES } from '@/lib/constants';
import type { PrintDay } from '@/pages/timetable/utils';

function escapeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

type PrintTimetableOptions = {
  title: string;
  subtitle?: string;
  days: PrintDay[];
};

// 선생님별 / 통합 주간 시간표를 인쇄해요.
// 팝업이 막히는 환경에서도 동작하도록 숨김 iframe을 사용해요.
export function printWeeklyTimetable(options: PrintTimetableOptions): void {
  const { title, subtitle, days } = options;

  const headerCells = days
    .map((d) => `<th>${escapeHtml(DAY_NAMES[d.day])}요일</th>`)
    .join('');

  const bodyCells = days
    .map((d) => {
      const items = d.entries.length
        ? d.entries
            .map((e) => {
              const lines: string[] = [
                `<div class="cname">${escapeHtml(e.className)}</div>`,
              ];
              if (e.subject) lines.push(`<div class="subj">${escapeHtml(e.subject)}</div>`);
              lines.push(`<div class="time">${escapeHtml(e.time)}</div>`);
              if (e.teacher) lines.push(`<div class="teacher">담당 ${escapeHtml(e.teacher)}</div>`);
              if (e.students && e.students.length) {
                lines.push(
                  `<div class="students">${e.students.map((n) => escapeHtml(n)).join(', ')}</div>`
                );
              }
              return `<div class="item">${lines.join('')}</div>`;
            })
            .join('')
        : '<div class="empty">-</div>';
      return `<td>${items}</td>`;
    })
    .join('');

  const html = `<!doctype html>
<html lang="ko">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>
  @page { size: A4 landscape; margin: 12mm; }
  * { box-sizing: border-box; }
  body { margin: 0; padding: 16px; font-family: 'Malgun Gothic', 'Apple SD Gothic Neo', sans-serif; color: #111; }
  .head { display: flex; align-items: flex-end; justify-content: space-between; margin-bottom: 12px; }
  h1 { font-size: 20px; margin: 0; }
  .sub { font-size: 12px; color: #666; margin-top: 4px; }
  .date { font-size: 11px; color: #888; }
  table { width: 100%; border-collapse: collapse; table-layout: fixed; }
  th, td { border: 1px solid #ccc; vertical-align: top; }
  th { background: #f3f4f6; font-size: 12px; padding: 6px 4px; text-align: center; }
  td { padding: 5px; }
  .item { border: 1px solid #e2e2e2; border-left: 3px solid #16a34a; border-radius: 4px; padding: 5px 6px; margin-bottom: 5px; }
  .item .cname { font-size: 12px; font-weight: 700; }
  .item .subj { font-size: 10px; color: #666; margin-top: 1px; }
  .item .time { font-size: 11px; font-weight: 600; color: #15803d; margin-top: 2px; }
  .item .teacher { font-size: 10px; color: #555; margin-top: 2px; }
  .item .students { font-size: 10px; color: #888; margin-top: 3px; border-top: 1px dashed #eee; padding-top: 3px; }
  .empty { text-align: center; color: #bbb; font-size: 12px; padding: 10px 0; }
</style>
</head>
<body>
  <div class="head">
    <div>
      <h1>${escapeHtml(title)}</h1>
      ${subtitle ? `<div class="sub">${escapeHtml(subtitle)}</div>` : ''}
    </div>
    <div class="date">출력일 ${new Date().toLocaleDateString('ko-KR')}</div>
  </div>
  <table>
    <thead><tr>${headerCells}</tr></thead>
    <tbody><tr>${bodyCells}</tr></tbody>
  </table>
</body>
</html>`;

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  iframe.style.visibility = 'hidden';
  document.body.appendChild(iframe);

  const removeIframe = () => {
    setTimeout(() => {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    }, 1500);
  };

  const runPrint = () => {
    const win = iframe.contentWindow;
    if (!win) {
      removeIframe();
      return;
    }
    try {
      win.focus();
      win.print();
    } catch (err) {
      console.error(err);
    }
    removeIframe();
  };

  const doc = iframe.contentWindow?.document;
  if (!doc) {
    removeIframe();
    return;
  }

  doc.open();
  doc.write(html);
  doc.close();

  // 내용이 그려진 뒤 인쇄되도록 살짝 기다려요.
  setTimeout(runPrint, 400);
}